import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { ClioService, ManualTaskAssignmentReportItem } from "../../api";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

const MySwal = withReactContent(Swal);

const DATE_PRESETS = [
  { value: "All", label: "All" },
  { value: "Today", label: "Today" },
  { value: "Week", label: "This week" },
  { value: "Month", label: "This month" },
  { value: "LastMonth", label: "Last month" },
] as const;

type DatePreset = (typeof DATE_PRESETS)[number]["value"] | "Custom";

type Props = {
  onAssignClick: () => void;
  refreshKey?: number;
};

function getDatesForPreset(preset: DatePreset): { start: Dayjs | null; end: Dayjs | null } {
  const now = dayjs();
  switch (preset) {
    case "All":
      return { start: null, end: null };
    case "Today":
      return { start: now.startOf("day"), end: now };
    case "Week":
      return { start: now.startOf("week"), end: now };
    case "Month":
      return { start: now.startOf("month"), end: now };
    case "LastMonth":
      return {
        start: now.startOf("month").subtract(1, "month"),
        end: now.startOf("month").subtract(1, "day"),
      };
    default:
      return { start: null, end: null };
  }
}

export default function ManualTaskAssignmentReport({ onAssignClick, refreshKey = 0 }: Props) {
  const [reportData, setReportData] = useState<ManualTaskAssignmentReportItem[]>([]);
  const [selectedPreset, setSelectedPreset] = useState<DatePreset>("Month");
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().startOf("month"));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const loadReport = useCallback(() => {
    setLoading(true);
    ClioService.getApiClioManualTaskAssignmentReport(
      startDate ? startDate.format("YYYY-MM-DD") : undefined,
      endDate ? endDate.format("YYYY-MM-DD") : undefined
    )
      .then((data) => {
        setReportData(data);
        setPage(0);
      })
      .catch(() =>
        MySwal.fire({
          icon: "error",
          title: <p>Error loading task assignment report.</p>,
        })
      )
      .finally(() => setLoading(false));
  }, [startDate, endDate]);

  useEffect(() => {
    loadReport();
  }, [loadReport, refreshKey]);

  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) {
      return reportData;
    }
    const lower = searchTerm.toLowerCase();
    return reportData.filter(
      (row) =>
        row.createdByUserName?.toLowerCase().includes(lower) ||
        row.matterName?.toLowerCase().includes(lower) ||
        row.taskName?.toLowerCase().includes(lower) ||
        row.assigneeName?.toLowerCase().includes(lower)
    );
  }, [reportData, searchTerm]);

  const paginatedData = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handlePresetChange = (preset: DatePreset) => {
    setSelectedPreset(preset);
    const { start, end } = getDatesForPreset(preset);
    setStartDate(start);
    setEndDate(end);
  };

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Clio Task Assignments
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Track tasks created manually through GLio.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onAssignClick}
          sx={{ alignSelf: { xs: "stretch", sm: "center" }, whiteSpace: "nowrap" }}
        >
          Assign Task
        </Button>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
          <FormControl size="small" sx={{ width: 180 }}>
            <InputLabel id="date-preset-label">Quick filter</InputLabel>
            <Select
              labelId="date-preset-label"
              label="Quick filter"
              value={selectedPreset}
              onChange={(e) => handlePresetChange(e.target.value as DatePreset)}
            >
              {DATE_PRESETS.map((preset) => (
                <MenuItem key={preset.value} value={preset.value}>
                  {preset.label}
                </MenuItem>
              ))}
              {selectedPreset === "Custom" && (
                <MenuItem value="Custom" disabled>
                  Custom
                </MenuItem>
              )}
            </Select>
          </FormControl>
          <DatePicker
            label="Start date"
            value={startDate}
            onChange={(date) => {
              setStartDate(date);
              setSelectedPreset("Custom");
            }}
            slotProps={{
              textField: {
                size: "small",
                sx: { width: 180 },
              },
            }}
          />
          <DatePicker
            label="End date"
            value={endDate}
            onChange={(date) => {
              setEndDate(date);
              setSelectedPreset("Custom");
            }}
            slotProps={{
              textField: {
                size: "small",
                sx: { width: 180 },
              },
            }}
          />
          <TextField
            placeholder="Search assignments..."
            size="small"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ width: { xs: "100%", sm: 280 }, flexGrow: { sm: 1 }, minWidth: 220 }}
          />
          <Button
            variant="contained"
            size="small"
            onClick={loadReport}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{ height: 40, minWidth: 130, px: 2.5 }}
          >
            Load Report
          </Button>
        </Box>
      </Paper>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "action.hover" }}>
              <TableCell sx={{ fontWeight: 600, width: 56 }}>No</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Matter</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Task</TableCell>
              <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>Date Generated</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Assigned To</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Created By</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && reportData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={32} />
                </TableCell>
              </TableRow>
            ) : paginatedData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  No task assignments found for the selected filters.
                </TableCell>
              </TableRow>
            ) : (
              paginatedData.map((row, index) => (
                <TableRow key={row.id} hover>
                  <TableCell sx={{ color: "text.secondary" }}>
                    {page * rowsPerPage + index + 1}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 280 }}>{row.matterName}</TableCell>
                  <TableCell sx={{ maxWidth: 220 }}>{row.taskName}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {row.createdAt
                      ? dayjs(row.createdAt).format("MM/DD/YYYY h:mm A")
                      : "—"}
                  </TableCell>
                  <TableCell>{row.assigneeName}</TableCell>
                  <TableCell>{row.createdByUserName}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </TableContainer>
    </Box>
  );
}
